/**
 * PriceTag — unified renderer for every price displayed in the app.
 *
 * Three explicit tiers (mandated by the price-system audit):
 *   • LIVE       — Travelpayouts / partner API data. Shows a 🟢 dot + "Live"
 *                  label. The displayed price MUST match the affiliate redirect.
 *   • ESTIMATED  — Algorithmically derived from a curated base price (no live
 *                  API). Rendered with a "~" prefix and a tooltip disclaimer
 *                  saying the final price may vary on the partner site.
 *   • MARKETING  — Curated entry-point floor for featured packages /
 *                  hand-picked destinations. Rendered as "From €X" with a
 *                  small "promotional" hint.
 *
 * The price string itself is preserved as-is (so existing range formats like
 * "€60-€110/night" keep working) — we only add framing around it.
 *
 * Usage:
 *   <PriceTag price="€89" tier="live" />
 *   <PriceTag price="€100-€200" tier="estimated" suffix="/flight" />
 *   <PriceTag price="€299" tier="marketing" size="lg" />
 */
import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Sparkles, Info } from 'lucide-react';

const TIER_STYLE = {
  live: {
    wrap: 'text-emerald-700',
    badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    badgeLabel: 'Live',
    dotClass: 'bg-emerald-500',
    tooltip: 'Live partner price · matches the booking page',
  },
  estimated: {
    wrap: 'text-blue-700',
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    badgeLabel: 'Est.',
    dotClass: '',
    tooltip: 'Estimated price · final price may vary on the partner site',
  },
  marketing: {
    wrap: 'text-blue-700',
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    badgeLabel: 'From',
    dotClass: '',
    tooltip: 'Promotional entry-point price · live availability on partner site',
  },
};

const TIER_BG = {
  live: { badgeLabel: 'От партньор', tooltip: 'Цена от доставчик; потвърди окончателната сума при резервация' },
  estimated: { badgeLabel: 'Ориент.', tooltip: 'Ориентировъчна цена; окончателната стойност не е потвърдена' },
  marketing: { badgeLabel: 'От', tooltip: 'Начална рекламна цена; наличността се проверява при доставчика' },
};

const SIZE_CLASSES = {
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl sm:text-2xl',
};

const stripLeadingFrom = (s) => String(s || '').replace(/^\s*from\s+/i, '');

export default function PriceTag({
  price,
  tier = 'marketing',
  size = 'base',
  suffix = '',
  showBadge = true,
  testId,
}) {
  const { language } = useLanguage();
  if (price === null || price === undefined || price === '') return null;
  const baseStyle = TIER_STYLE[tier] || TIER_STYLE.marketing;
  const style = language === 'bg'
    ? { ...baseStyle, ...(TIER_BG[tier] || TIER_BG.marketing) }
    : baseStyle;
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.base;

  // For ESTIMATED tier, prefix the price with "~" once (so "€100-€200" → "~€100-€200")
  // and don't break existing range strings.
  let displayPrice = stripLeadingFrom(price);
  if (tier === 'estimated' && !displayPrice.trim().startsWith('~')) {
    displayPrice = `~${displayPrice}`;
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 font-bold ${style.wrap} ${sizeClass}`}
      title={style.tooltip}
      data-testid={testId}
      data-price-tier={tier}
    >
      {showBadge && (
        <span
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wide ${style.badge}`}
        >
          {tier === 'live' && (
            <span className={`w-1.5 h-1.5 rounded-full ${style.dotClass} animate-pulse`} />
          )}
          {style.badgeLabel}
        </span>
      )}
      <span>{displayPrice}</span>
      {suffix && <span className="font-normal text-xs text-gray-500">{suffix}</span>}
    </span>
  );
}

/**
 * PriceTierDisclaimer — small explanatory line rendered below a price block.
 * Use sparingly; the inline badge already conveys the tier. Most useful on
 * package detail pages and the Smart Deals strip where users compare totals.
 */
export function PriceTierDisclaimer({ tier, className = '' }) {
  const { language } = useLanguage();
  const baseStyle = TIER_STYLE[tier] || TIER_STYLE.marketing;
  const style = language === 'bg'
    ? { ...baseStyle, ...(TIER_BG[tier] || TIER_BG.marketing) }
    : baseStyle;
  return (
    <p
      className={`text-[10px] text-gray-500 leading-snug flex items-start gap-1 ${className}`}
      data-testid={`price-disclaimer-${tier}`}
    >
      <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
      <span>{style.tooltip}</span>
    </p>
  );
}

/**
 * LivePriceBadge — small standalone badge for non-numeric "Live" indicators
 * (e.g. a Smart Deal card's corner). Kept here so the visual language stays
 * unified with PriceTag.
 */
export function LivePriceBadge({ className = '' }) {
  const { language } = useLanguage();
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px] font-semibold ${className}`}
    >
      <Sparkles className="w-3 h-3" />
      {language === 'bg' ? 'От партньор' : 'Live'}
    </span>
  );
}
